import './hud.css';
import { el, setText, text } from './walkout.js';
import { needs } from './contract.js';

/**
 * The read-out a fighting game has while people are fighting.
 *
 * Everything on it was already visible — in the **debug overlay**, which is a
 * developer's window: a column of labelled numbers pinned to the left of the
 * screen, in the same panel as the state graph and the arena swap. Two
 * consciousness values to three decimal places is a good way to find a bug in
 * the health model and a bad way to know whether you are winning.
 *
 * So this is the same data, read the way a player reads it: two bars facing
 * each other, a clock between them, and the names of the two people it is
 * about. The overlay is unchanged and still has the decimals; it has simply
 * stopped being the only way to see any of it, and now starts collapsed.
 *
 * **The ceiling is the point of the health bar.** Consciousness is what a blow
 * takes and it grows back; wellness is the roof it grows back *to*, and it
 * never does. A bar without the ceiling drawn behind it is a fighter who looks
 * like they recovered, and the whole two-layer model — the thing the game has
 * been built around since step 4 — is invisible. It is one extra fill and it is
 * the difference between a health bar and *this* game's health bar.
 *
 * It shows itself while the round is live and hides otherwise, off `match.live`
 * and nothing else. The walkout holds the round in `intro` and the aftermath
 * comes after `over`, so the HUD is never fighting a full-bleed screen for the
 * same pixels and nobody has to remember to hide it.
 */
export function createHud({ player, opponent, match, container = document.body }) {
  needs(match, ['live', 'phase', 'remaining'], { who: 'the HUD', what: 'match' });
  for (const [side, who] of [['player', player], ['opponent', opponent]]) {
    needs(who, ['id', 'name', 'health', 'momentum', 'heat', 'armed', 'blazin'], {
      who: 'the HUD',
      what: side,
    });
    needs(who.health, ['fractions', 'inDanger'], { who: 'the HUD', what: `${side}.health` });
    needs(who.momentum, ['fraction', 'canIgnite'], { who: 'the HUD', what: `${side}.momentum` });
    needs(who.heat, ['fraction', 'level'], { who: 'the HUD', what: `${side}.heat` });
  }
  const root = el('div', 'hud');
  root.hidden = true;

  const mine = corner(player, 'mine');
  const clock = middle();
  const theirs = corner(opponent, 'theirs');
  root.append(mine.root, clock.root, theirs.root);
  container.append(root);

  return {
    element: root,
    get up() {
      return !root.hidden;
    },

    /**
     * One frame. Called from the drawn half rather than from `advance`, because
     * nothing here is a rule — a HUD that did not update during a hitstop would
     * be a HUD whose numbers lag the freeze they are describing.
     */
    update() {
      const live = Boolean(match?.live);
      if (root.hidden === live) root.hidden = !live;
      if (!live) return false;
      mine.update();
      theirs.update();
      clock.update(match);
      return true;
    },

    dispose() {
      root.remove();
    },
  };
}

/** One fighter's side of it: a name, two meters, and the flags that matter. */
function corner(who, side) {
  const root = el('div', `hud__corner hud__corner--${side}`);

  const head = el('div', 'hud__head');
  const name = text('div', 'hud__name', '');
  const flags = text('div', 'hud__flags', '');
  head.append(name, flags);

  const health = bar('hud__health', { ceiling: true });
  const meters = el('div', 'hud__meters');
  const momentum = bar('hud__momentum');
  const heat = bar('hud__heat');
  meters.append(momentum.root, heat.root);

  root.append(head, health.root, meters);

  return {
    root,
    update() {
      setText(name, who.name ?? who.id);

      const bars = who.health.fractions();
      health.set(bars.consciousness, bars.ceiling);
      // In danger is the one state on here that changes what the *rules* do —
      // it is the gate every finisher asks about — so it is the one thing
      // allowed to recolour a bar.
      root.dataset.danger = String(Boolean(who.health.inDanger));

      momentum.set(who.momentum?.fraction ?? 0);
      heat.set(who.heat?.fraction ?? 0);
      root.dataset.blazin = String(Boolean(who.blazin));
      root.dataset.room = who.heat?.level ?? 'warm';

      // Three words at most, and only when they are true. A permanent legend
      // would be three more things to read while somebody is hitting you.
      const said = [];
      if (who.blazin) said.push('blazin');
      else if (who.momentum?.canIgnite) said.push('ready');
      if (who.armed) said.push(who.weapon?.id ?? 'armed');
      if (who.health.inDanger) said.push('hurt');
      setText(flags, said.join(' · '));
    },
  };
}

/** The clock, and what the round is doing. */
function middle() {
  const root = el('div', 'hud__clock');
  const value = text('div', 'hud__seconds', '');
  const phase = text('div', 'hud__phase', '');
  root.append(value, phase);

  return {
    root,
    update(match) {
      const left = match.remaining ?? 0;
      setText(value, String(Math.ceil(left)));
      // The last ten seconds are the only time the clock is worth looking at,
      // and a decision is decided on bars nobody can read in a hurry.
      root.dataset.urgent = String(left > 0 && left <= 10);
      setText(phase, match.live ? 'round 1' : match.phase);
    },
  };
}

/**
 * A meter. `ceiling` adds the second fill behind the first, which is what makes
 * a health bar in this game different from a health bar.
 */
function bar(className, { ceiling = false } = {}) {
  const root = el('div', `ui-bar ${className}`);
  const roof = ceiling ? el('div', 'ui-bar__ceiling') : null;
  const fill = el('div', 'ui-bar__fill');
  if (roof) root.append(roof);
  root.append(fill);

  return {
    root,
    set(value, top = null) {
      const width = `${Math.max(0, Math.min(1, value)) * 100}%`;
      if (fill.style.width !== width) fill.style.width = width;
      if (!roof) return;
      const cap = `${Math.max(0, Math.min(1, top ?? value)) * 100}%`;
      if (roof.style.width !== cap) roof.style.width = cap;
    },
  };
}
